import { Alert, Box, Button, Stack, Typography } from "@mui/material";
import { useQuery } from "react-query";
import { z } from "zod";
import { getAuthenticatedServerClient, schemas } from "../../../api/server.api";
import { grants } from "../../../backoffice.access_control";
import { overviewCardStyle } from "../../../misc/backofficeTheme";
import type { Student } from "../../../providers/studentsProvider";
import { formatDate } from "../../../utils/calendar";

type StudentCourse = z.infer<typeof schemas.StudentCourseDto>;
type CourseModule = StudentCourse["modules"][number];

const formatLessonUnits = (units: number) => units.toLocaleString("de-DE", { maximumFractionDigits: 2 });

export function StudentCourseProgressCards(props: { student: Student }) {
  const { student } = props;
  const canListCourses = grants.has("Course:list");
  const {
    data: courses,
    isLoading,
    isError,
    refetch,
  } = useQuery(
    ["studentCourses", student.id],
    async () => (await getAuthenticatedServerClient()).getStudentCourses({ params: { id: student.id } }),
    {
      enabled: canListCourses,
      refetchOnMount: "always",
      onError: (error) => handleError("Der Kursfortschritt konnte nicht geladen werden.", error),
    },
  );

  if (!canListCourses) return null;

  if (isLoading) {
    return (
      <Box className="StudentOverviewTab-courseProgress" sx={overviewCardStyle}>
        <Typography color="text.secondary">Kursfortschritt wird geladen…</Typography>
      </Box>
    );
  }

  if (isError) {
    return (
      <Box className="StudentOverviewTab-courseProgress" sx={overviewCardStyle}>
        <Alert
          severity="error"
          action={
            <Button onClick={() => void refetch()} color="inherit">
              Erneut laden
            </Button>
          }
        >
          Der Kursfortschritt konnte nicht geladen werden.
        </Alert>
      </Box>
    );
  }

  if (!courses?.length) return null;

  return (
    <Stack className="StudentOverviewTab-courseProgress" spacing="20px">
      {courses.map((course) => (
        <CourseProgressCard key={course.bookedQuoteId} course={course} />
      ))}
    </Stack>
  );
}

export function CourseProgressCard(props: { course: StudentCourse }) {
  const { course } = props;
  const totalLessonUnits = course.modules.reduce((total, module) => total + module.moduleLessonUnits, 0);

  return (
    <Box sx={{ ...overviewCardStyle, padding: "24px 30px" }}>
      <Typography sx={{ fontWeight: "bold", fontSize: 15, overflowWrap: "anywhere" }}>{course.name}</Typography>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
          gap: 1,
          mt: 2,
          mb: 2,
        }}
      >
        {[
          ["Beginn", course.startDate ? formatDate(course.startDate) : "–"],
          ["Ende", course.endDate ? formatDate(course.endDate) : "–"],
          ["Gesamt UE", formatLessonUnits(totalLessonUnits)],
        ].map(([label, value]) => (
          <Box key={label} sx={{ minWidth: 0 }}>
            <Typography color="text.secondary" sx={{ fontSize: 12 }}>
              {label}
            </Typography>
            <Typography sx={{ fontWeight: "bold", overflowWrap: "anywhere" }}>{value}</Typography>
          </Box>
        ))}
      </Box>
      <Stack spacing={2}>
        {course.modules.map((module) => (
          <CourseModuleProgress key={module.moduleName} module={module} />
        ))}
      </Stack>
    </Box>
  );
}

function CourseModuleProgress(props: { module: CourseModule }) {
  const { module } = props;
  const { attendedLessonUnits, missedLessonUnits, moduleLessonUnits } = module;
  const barTotal = Math.max(moduleLessonUnits, attendedLessonUnits + missedLessonUnits);
  const attendedWidth = (attendedLessonUnits / barTotal) * 100;
  const missedWidth = (missedLessonUnits / barTotal) * 100;

  return (
    <Box>
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0, mb: 1 }}>
        <Box sx={{ width: 12, height: 12, borderRadius: "50%", bgcolor: module.moduleColor, flexShrink: 0 }} />
        <Typography
          title={module.moduleName}
          sx={{
            fontWeight: "bold",
            fontSize: 15,
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {module.moduleName}
        </Typography>
      </Stack>
      <Box
        role="progressbar"
        aria-label={`Fortschritt ${module.moduleName}`}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(attendedWidth + missedWidth)}
        aria-valuetext={`${formatLessonUnits(attendedLessonUnits)} UE anwesend, ${formatLessonUnits(missedLessonUnits)} UE abwesend von ${formatLessonUnits(moduleLessonUnits)} UE`}
        sx={{ height: 10, borderRadius: 5, bgcolor: "action.disabledBackground", display: "flex", overflow: "hidden" }}
      >
        <Box sx={{ bgcolor: "primary.main", flexShrink: 0 }} style={{ width: `${attendedWidth}%` }} />
        <Box sx={{ bgcolor: "destructiveAction.main", flexShrink: 0 }} style={{ width: `${missedWidth}%` }} />
      </Box>
      <Stack direction="row" justifyContent="space-between" spacing={1} sx={{ mt: 0.75 }}>
        <Box>
          <Typography component="span" color="primary.main" sx={{ fontSize: 12 }}>
            {formatLessonUnits(attendedLessonUnits)} Anw.
          </Typography>
          {missedLessonUnits > 0 && (
            <Typography component="span" color="destructiveAction.main" sx={{ ml: 1, fontSize: 12 }}>
              {formatLessonUnits(missedLessonUnits)} Abw.
            </Typography>
          )}
        </Box>
        <Typography color="text.secondary" sx={{ flexShrink: 0, fontSize: 12 }}>
          von {formatLessonUnits(moduleLessonUnits)} UE
        </Typography>
      </Stack>
    </Box>
  );
}
